/**
 * Report assembly and export.
 *
 * The report merges three sources of truth, each labelled so the reader always
 * knows how much to trust a statement:
 *   - deterministic checks (observable in the submission)
 *   - OpenAPI validation (the specification is the contract)
 *   - model analysis (helpful, but never evidence)
 */

import { buildCurl } from './curl';
import type {
  CorrectedExample,
  DeterministicFinding,
  DiagnosisReport,
  InputType,
  LikelyCause,
  OpenApiValidationReport,
  ParsedEvidence,
  ProviderAnalysis,
  SanitizationReport,
  TroubleshootingStep,
} from './types';

export const REPORT_VERSION = 1 as const;

export const SAFETY_NOTES: readonly string[] = [
  'API Rescue Desk never executes the submitted request, curl command or payload, and never fetches a URL taken from your input.',
  'Model output is treated as advisory. It is schema-checked and rendered as plain text, and it is never executed.',
  'Redaction is best-effort. Review the sanitized text before saving, exporting or sharing a report.',
  'Never paste production secrets into a third-party tool unless you have reviewed what will be stored.',
];

export const LIMITATIONS: readonly string[] = [
  'Findings are derived from the text you submitted. Anything not present in that text cannot be diagnosed.',
  'Confidence scores are heuristics, not measured probabilities.',
  'No response field, endpoint or documentation detail is invented: if a detail is missing, the report says so.',
  'OpenAPI validation covers structure, required fields, types, enums and documented status codes. It does not prove runtime behaviour.',
];

/* -------------------------------------------------------------------------- */
/* Merge                                                                       */
/* -------------------------------------------------------------------------- */

const KIND_RANK: Record<string, number> = { confirmed: 0, likely: 1, speculative: 2 };

function slug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
}

export interface MergeInput {
  detectedInputType: InputType;
  inputTypeSource: 'auto' | 'manual';
  sanitization: SanitizationReport;
  evidence: ParsedEvidence;
  deterministicFindings: DeterministicFinding[];
  openApiValidation: OpenApiValidationReport;
  providerAnalysis: ProviderAnalysis | null;
  provider: {
    name: string;
    model: string | null;
    mode: 'user_key' | 'platform' | 'mock' | 'none';
    usedAi: boolean;
    latencyMs: number | null;
  };
  generatedAt?: Date;
}

export function mergeReport(input: MergeInput): DiagnosisReport {
  const generatedAt = (input.generatedAt ?? new Date()).toISOString();

  const openApiCauses: LikelyCause[] = input.openApiValidation.findings.map((finding, index) => ({
    id: `openapi-${index}-${slug(finding.code)}`,
    title: finding.message,
    confidence: finding.kind === 'confirmed' ? 88 : finding.kind === 'likely' ? 60 : 30,
    kind: finding.kind,
    category: 'openapi_contract',
    explanation:
      'The supplied OpenAPI specification is the contract for this operation, so this is a specification-level statement rather than a guess.',
    evidence: [`Location: ${finding.location}`, `Specification check: ${finding.code}`],
    source: 'openapi',
  }));

  const deterministicCauses: LikelyCause[] = input.deterministicFindings.map((finding) => ({
    id: `det-${slug(finding.code)}`,
    title: finding.title,
    confidence: finding.kind === 'confirmed' ? 85 : finding.kind === 'likely' ? 60 : 30,
    kind: finding.kind,
    category: finding.category,
    explanation: finding.detail,
    evidence: finding.evidence,
    source: 'deterministic',
  }));

  const modelCauses: LikelyCause[] = (input.providerAnalysis?.likelyCauses ?? []).map(
    (cause, index) => ({
      id: `model-${index}-${slug(cause.title)}`,
      title: cause.title,
      confidence: cause.confidence,
      kind: cause.kind,
      category: cause.category,
      explanation: cause.explanation,
      evidence: cause.evidence,
      source: 'model' as const,
    }),
  );

  const causes = dedupeCauses([...openApiCauses, ...deterministicCauses, ...modelCauses]);
  causes.sort(
    (a, b) =>
      (KIND_RANK[a.kind] ?? 3) - (KIND_RANK[b.kind] ?? 3) ||
      b.confidence - a.confidence ||
      a.title.localeCompare(b.title),
  );

  const mostLikelyRootCause = causes[0] ?? null;

  const troubleshootingSteps = buildSteps(input, causes);
  const correctedExample = buildCorrectedExample(input, causes);
  const counts = {
    confirmed: causes.filter((c) => c.kind === 'confirmed').length,
    likely: causes.filter((c) => c.kind === 'likely').length,
    speculative: causes.filter((c) => c.kind === 'speculative').length,
  };

  const report: DiagnosisReport = {
    version: REPORT_VERSION,
    detectedInputType: input.detectedInputType,
    inputTypeSource: input.inputTypeSource,
    generatedAt,
    executiveSummary: buildExecutiveSummary(input, causes, counts),
    mostLikelyRootCause,
    likelyCauses: causes.slice(0, 12),
    deterministicFindings: input.deterministicFindings,
    openApiValidation: input.openApiValidation,
    troubleshootingSteps,
    correctedExample,
    verificationChecklist: buildVerificationChecklist(input, causes),
    informationStillNeeded: buildInformationNeeded(input),
    supportTicketSummary: '',
    uncertaintyNotes: buildUncertaintyNotes(input, counts),
    safetyNotes: [...SAFETY_NOTES],
    limitations: [...LIMITATIONS],
    sanitization: input.sanitization,
    provider: input.provider,
    counts,
  };

  report.supportTicketSummary = buildSupportTicketSummary(report);
  return report;
}

function dedupeCauses(causes: LikelyCause[]): LikelyCause[] {
  const byKey = new Map<string, LikelyCause>();
  for (const cause of causes) {
    const key = slug(cause.title);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, cause);
      continue;
    }
    // Prefer the more evidential source and keep the richer evidence list.
    const sourceRank: Record<string, number> = { openapi: 0, deterministic: 1, model: 2 };
    const preferred =
      (sourceRank[cause.source] ?? 3) < (sourceRank[existing.source] ?? 3) ? cause : existing;
    byKey.set(key, {
      ...preferred,
      confidence: Math.max(existing.confidence, cause.confidence),
      evidence: [...new Set([...existing.evidence, ...cause.evidence])].slice(0, 8),
      explanation:
        existing.explanation.length >= cause.explanation.length
          ? existing.explanation
          : cause.explanation,
    });
  }
  return [...byKey.values()];
}

function buildExecutiveSummary(
  input: MergeInput,
  causes: LikelyCause[],
  counts: { confirmed: number; likely: number; speculative: number },
): string {
  const modelSummary = input.providerAnalysis?.executiveSummary?.trim();
  const deterministicLead =
    causes.length === 0
      ? 'No deterministic rule matched this submission, so the analysis is deliberately conservative.'
      : `The strongest signal is “${causes[0]!.title}” (${causes[0]!.kind}).`;

  const tally = `Findings: ${counts.confirmed} confirmed, ${counts.likely} likely, ${counts.speculative} speculative.`;

  if (modelSummary) {
    return `${modelSummary}\n\n${deterministicLead} ${tally}`;
  }
  return `${deterministicLead} ${tally}`;
}

function buildSteps(input: MergeInput, causes: LikelyCause[]): TroubleshootingStep[] {
  const steps: TroubleshootingStep[] = [];
  const seen = new Set<string>();

  const push = (title: string, detail: string, whyItMatters: string) => {
    const key = slug(title);
    if (key.length === 0 || seen.has(key)) return;
    seen.add(key);
    steps.push({ order: steps.length + 1, title, detail, whyItMatters });
  };

  // OpenAPI contract violations first: the specification is authoritative.
  for (const finding of input.openApiValidation.findings) {
    if (finding.severity !== 'error') continue;
    push(
      `Fix the specification violation at ${finding.location}`,
      finding.message,
      'The supplied OpenAPI specification is the contract the API is expected to enforce, so resolving this removes the most likely cause first.',
    );
    if (steps.length >= 4) break;
  }

  for (const finding of input.deterministicFindings) {
    for (const suggestion of finding.suggestions) {
      push(suggestion, suggestion, `Derived from the ${finding.kind} finding \`${finding.code}\`.`);
      if (steps.length >= 10) break;
    }
    if (steps.length >= 10) break;
  }

  for (const step of input.providerAnalysis?.troubleshootingSteps ?? []) {
    push(
      step.title,
      step.detail || step.title,
      step.whyItMatters || 'Suggested by the configured AI provider.',
    );
    if (steps.length >= 12) break;
  }

  if (steps.length === 0) {
    push(
      'Add the failing HTTP response (status line plus headers) and re-run the diagnosis',
      'The deterministic rules read status codes, headers, transport error signatures and parse failures. Adding the response usually produces a concrete finding.',
      'Without a status code or a transport error signature there is very little to reason from.',
    );
  }

  if (causes.length > 0) {
    push(
      'Re-run the diagnosis after applying one change at a time',
      'Changing several things at once makes it impossible to tell which change fixed the failure.',
      'Isolating variables is the fastest route to a confirmed root cause.',
    );
  }

  return steps.map((step, index) => ({ ...step, order: index + 1 })).slice(0, 12);
}

function buildCorrectedExample(input: MergeInput, causes: LikelyCause[]): CorrectedExample | null {
  const { evidence } = input;
  const authProblem = causes.some(
    (cause) => cause.category === 'authentication' && cause.kind !== 'speculative',
  );
  const contentTypeProblem = input.deterministicFindings.some((finding) =>
    finding.code.includes('CONTENT_TYPE'),
  );

  if (evidence.curl) {
    const addHeaders: Array<{ name: string; value: string }> = [];
    const headers = evidence.curl.headers;
    const hasAuth = headers.some((h) => h.name.toLowerCase() === 'authorization');
    const bodyIsJson = (evidence.curl.body ?? '').trim().startsWith('{');

    if (authProblem && !hasAuth) {
      addHeaders.push({ name: 'Authorization', value: 'Bearer <YOUR_TOKEN>' });
    }
    if (
      bodyIsJson &&
      (contentTypeProblem || !headers.some((h) => h.name.toLowerCase() === 'content-type'))
    ) {
      addHeaders.push({ name: 'Content-Type', value: 'application/json' });
    }

    let body = evidence.curl.body;
    if (bodyIsJson && body) {
      try {
        body = JSON.stringify(JSON.parse(body), null, 2);
      } catch {
        // Not valid JSON after all: keep the original text unchanged.
      }
    }

    // Headers explicitly fixed by the evidence replace the originals.
    const correctedHeaders =
      bodyIsJson && contentTypeProblem
        ? headers.filter((header) => header.name.toLowerCase() !== 'content-type')
        : headers;

    return {
      format: 'curl',
      title: 'Corrected curl command',
      content: buildCurl(evidence.curl, { headers: correctedHeaders, addHeaders, body }),
      notes: [
        'Only changes supported by the evidence were applied. Anything inside <ANGLE_BRACKETS> is a value you must supply.',
        'API Rescue Desk did not execute this command.',
      ],
      generatedBy: 'deterministic',
    };
  }

  if (evidence.http?.kind === 'request') {
    const lines: string[] = [];
    const method = evidence.http.method ?? 'POST';
    const target = evidence.http.target ?? '/path';
    lines.push(`${method} ${target} HTTP/1.1`);

    const bodyIsJson = (evidence.http.body ?? '').trim().startsWith('{');
    const hasAuth = evidence.http.headers.some((h) => h.name.toLowerCase() === 'authorization');
    const hasContentType = evidence.http.headers.some(
      (h) => h.name.toLowerCase() === 'content-type',
    );

    const headers = evidence.http.headers.filter(
      (header) =>
        !(bodyIsJson && contentTypeProblem && header.name.toLowerCase() === 'content-type'),
    );
    if (authProblem && !hasAuth)
      headers.push({ name: 'Authorization', value: 'Bearer <YOUR_TOKEN>' });
    if (bodyIsJson && (contentTypeProblem || !hasContentType)) {
      headers.push({ name: 'Content-Type', value: 'application/json' });
    }

    for (const header of headers) lines.push(`${header.name}: ${header.value}`);
    lines.push('');
    if (evidence.http.body) {
      let body = evidence.http.body;
      if (bodyIsJson) {
        try {
          body = JSON.stringify(JSON.parse(body), null, 2);
        } catch {
          // Not valid JSON after all: keep the original text unchanged.
        }
      }
      lines.push(body);
    }

    return {
      format: 'http',
      title: 'Corrected HTTP request',
      content: lines.join('\n'),
      notes: [
        'Headers that were already present were preserved, except where a confirmed finding required a change. Placeholders in <ANGLE_BRACKETS> must be filled in.',
        'API Rescue Desk did not send this request.',
      ],
      generatedBy: 'deterministic',
    };
  }

  if (
    evidence.payload?.valid &&
    evidence.payload.format === 'json' &&
    evidence.payload.value !== null
  ) {
    return {
      format: 'json',
      title: 'Re-formatted payload',
      content: JSON.stringify(evidence.payload.value, null, 2),
      notes: [
        'This is your payload re-formatted for review only; no field was added, removed or corrected.',
      ],
      generatedBy: 'deterministic',
    };
  }

  const modelExample = input.providerAnalysis?.correctedExample;
  if (modelExample && modelExample.content.trim().length > 0) {
    return {
      format: modelExample.format,
      title: modelExample.title,
      content: modelExample.content,
      notes: [
        ...modelExample.notes,
        'This example was suggested by the configured AI provider and has not been executed or verified.',
      ],
      generatedBy: 'model',
    };
  }

  return null;
}

function buildVerificationChecklist(input: MergeInput, causes: LikelyCause[]): string[] {
  const items: string[] = [];
  const modelItems = input.providerAnalysis?.verificationChecklist ?? [];

  if (causes.length > 0) {
    items.push('The change you applied maps to one specific cause in the ranked list above.');
    items.push('You have recorded the status code before and after the change.');
  }
  for (const item of modelItems) items.push(item);

  const categories = new Set(causes.map((cause) => cause.category));
  if (categories.has('authentication') || categories.has('authorisation')) {
    items.push(
      'The request that succeeds uses a credential issued for the same environment as the endpoint.',
    );
  }
  if (categories.has('request_format')) {
    items.push('A strict JSON parser accepts the payload you are sending.');
  }
  if (categories.has('openapi_contract')) {
    items.push('The request now satisfies every specification finding listed in this report.');
  }
  if (input.evidence.webhook) {
    items.push('Replaying the same delivery twice does not double-apply the change (idempotency).');
  }

  items.push(
    'The sanitized paste below has been reviewed for anything sensitive that redaction missed.',
  );
  return [...new Set(items)].slice(0, 15);
}

function buildInformationNeeded(input: MergeInput): string[] {
  const items: string[] = [...(input.providerAnalysis?.informationStillNeeded ?? [])];
  const evidence = input.evidence;

  if (!evidence.http && evidence.apiError?.statusCode === null) {
    items.push('The HTTP status code of the failing response.');
  }
  if (!input.openApiValidation.available) {
    items.push(
      'An OpenAPI 3.x specification for this API, which enables contract-level validation.',
    );
  }
  if (input.sanitization.redacted) {
    items.push(
      'Confirmation that redaction did not remove a value that was itself the cause of the failure.',
    );
  }
  if (evidence.curl && evidence.curl.warnings.length > 0) {
    items.push(
      `Clarification of the curl parsing warnings: ${evidence.curl.warnings.slice(0, 3).join(' ')}`,
    );
  }

  return [...new Set(items)].slice(0, 12);
}

function buildUncertaintyNotes(
  input: MergeInput,
  counts: { confirmed: number; likely: number; speculative: number },
): string[] {
  const notes: string[] = [];
  if (counts.confirmed === 0 && counts.likely === 0) {
    notes.push(
      'Nothing in this report is confirmed or even strongly indicated; treat every item as a hypothesis.',
    );
  }
  if (counts.speculative > 0) {
    notes.push(
      `${counts.speculative} item${counts.speculative === 1 ? '' : 's'} below ${counts.speculative === 1 ? 'is' : 'are'} speculative and must be verified.`,
    );
  }
  if (!input.provider.usedAi) {
    notes.push('No AI provider was used for this report; only deterministic rules ran.');
  }
  if (!input.openApiValidation.available) {
    notes.push('No OpenAPI specification was supplied, so no contract validation was possible.');
  } else if (input.openApiValidation.depth === 'structural') {
    notes.push(
      'OpenAPI validation ran at structural depth only. Deep request/response validation is a Pro feature.',
    );
  }
  for (const note of input.providerAnalysis?.uncertaintyNotes ?? []) notes.push(note);
  return [...new Set(notes)].slice(0, 12);
}

/* -------------------------------------------------------------------------- */
/* Support ticket summary                                                     */
/* -------------------------------------------------------------------------- */

export function buildSupportTicketSummary(report: DiagnosisReport): string {
  const lines: string[] = [];
  lines.push('Summary');
  lines.push('-------');
  lines.push(truncate(firstParagraph(report.executiveSummary), 400));
  lines.push('');

  const method = report.deterministicFindings.find((f) => f.code.startsWith('HTTP_'))?.code ?? null;
  lines.push('Environment / request');
  lines.push('---------------------');
  lines.push(`Input type: ${report.detectedInputType}`);
  if (method) lines.push(`Reported status: ${method.replace('HTTP_', '')}`);
  lines.push(`Generated: ${report.generatedAt}`);
  lines.push('');

  if (report.mostLikelyRootCause) {
    lines.push('Most likely root cause');
    lines.push('----------------------');
    lines.push(
      `${report.mostLikelyRootCause.title} (${report.mostLikelyRootCause.kind}, confidence ${report.mostLikelyRootCause.confidence}%)`,
    );
    lines.push('');
  }

  const confirmed = report.likelyCauses.filter((c) => c.kind === 'confirmed');
  if (confirmed.length > 0) {
    lines.push('Confirmed findings');
    lines.push('------------------');
    for (const cause of confirmed.slice(0, 5)) lines.push(`- ${cause.title}`);
    lines.push('');
  }

  lines.push('What I have already tried');
  lines.push('-------------------------');
  for (const step of report.troubleshootingSteps.slice(0, 4)) lines.push(`- ${step.title}`);
  lines.push('');

  if (report.informationStillNeeded.length > 0) {
    lines.push('What I need from support');
    lines.push('------------------------');
    for (const item of report.informationStillNeeded.slice(0, 5)) lines.push(`- ${item}`);
    lines.push('');
  }

  lines.push(
    'Note: request data in this report has been sanitized; secrets were replaced with placeholders.',
  );
  lines.push(
    'Note: no request was executed by API Rescue Desk; findings are based on analysis of the pasted text.',
  );
  return lines.join('\n');
}

function firstParagraph(value: string): string {
  const index = value.indexOf('\n');
  return (index === -1 ? value : value.slice(0, index)).trim();
}

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max)}…`;
}

/* -------------------------------------------------------------------------- */
/* Exports                                                                    */
/* -------------------------------------------------------------------------- */

export function reportToJson(report: DiagnosisReport): string {
  return JSON.stringify(report, null, 2);
}

export function reportToMarkdown(report: DiagnosisReport): string {
  const lines: string[] = [];
  const heading = (text: string) => {
    lines.push('', `## ${text}`, '');
  };

  lines.push('# API Rescue Desk report');
  lines.push('');
  lines.push(`- Generated: ${report.generatedAt}`);
  lines.push(`- Detected input type: \`${report.detectedInputType}\` (${report.inputTypeSource})`);
  lines.push(
    `- Findings: ${report.counts.confirmed} confirmed, ${report.counts.likely} likely, ${report.counts.speculative} speculative`,
  );
  lines.push(
    `- Provider: \`${report.provider.name}\`${report.provider.model ? ` (${report.provider.model})` : ''} — ${report.provider.usedAi ? 'AI-assisted' : 'deterministic only'}`,
  );

  heading('Executive summary');
  lines.push(report.executiveSummary);

  heading('Most likely root cause');
  if (report.mostLikelyRootCause) {
    const cause = report.mostLikelyRootCause;
    lines.push(`**${cause.title}** — ${cause.kind}, confidence ${cause.confidence}%`);
    lines.push('');
    lines.push(cause.explanation);
    if (cause.evidence.length > 0) {
      lines.push('');
      lines.push('Evidence:');
      for (const item of cause.evidence) lines.push(`- ${item}`);
    }
  } else {
    lines.push('_No root cause could be identified from the submitted text._');
  }

  heading('Ranked likely causes');
  if (report.likelyCauses.length === 0) {
    lines.push('_None._');
  }
  for (const [index, cause] of report.likelyCauses.entries()) {
    lines.push(`### ${index + 1}. ${cause.title}`);
    lines.push('');
    lines.push(`- Confidence: ${cause.confidence}%`);
    lines.push(`- Classification: **${cause.kind}**`);
    lines.push(`- Category: \`${cause.category}\``);
    lines.push(`- Source: \`${cause.source}\``);
    lines.push('');
    if (cause.explanation) lines.push(cause.explanation, '');
    if (cause.evidence.length > 0) {
      lines.push('Evidence:');
      for (const item of cause.evidence) lines.push(`- ${item}`);
      lines.push('');
    }
  }

  heading('OpenAPI validation');
  if (!report.openApiValidation.available) {
    lines.push('_No OpenAPI specification was supplied for this diagnosis._');
    if (report.openApiValidation.reason) lines.push('', report.openApiValidation.reason);
  } else {
    lines.push(`- Specification: ${report.openApiValidation.specName ?? '(unnamed)'}`);
    lines.push(`- Validation depth: \`${report.openApiValidation.depth}\``);
    if (report.openApiValidation.matchedOperation) {
      const op = report.openApiValidation.matchedOperation;
      lines.push(
        `- Matched operation: \`${op.method} ${op.path}\`${op.operationId ? ` (${op.operationId})` : ''}`,
      );
    } else {
      lines.push(
        `- No operation matched \`${report.openApiValidation.requestedMethod ?? ''} ${report.openApiValidation.requestedPath ?? ''}\``,
      );
    }
    lines.push('');
    if (report.openApiValidation.findings.length === 0) {
      lines.push('_No specification violations were found in the parts that could be checked._');
    } else {
      for (const finding of report.openApiValidation.findings) {
        lines.push(
          `- **${finding.severity}** (${finding.kind}) \`${finding.location}\`: ${finding.message}`,
        );
      }
    }
    if (report.openApiValidation.checksPerformed.length > 0) {
      lines.push('', `Checks performed: ${report.openApiValidation.checksPerformed.join(', ')}.`);
    }
    if (report.openApiValidation.checksSkipped.length > 0) {
      lines.push('', `Checks skipped: ${report.openApiValidation.checksSkipped.join('; ')}.`);
    }
  }

  heading('Step-by-step troubleshooting');
  if (report.troubleshootingSteps.length === 0) {
    lines.push('_None._');
  }
  for (const step of report.troubleshootingSteps) {
    lines.push(`${step.order}. **${step.title}**`);
    if (step.detail && step.detail !== step.title) lines.push(`   - ${step.detail}`);
    if (step.whyItMatters) lines.push(`   - Why it matters: ${step.whyItMatters}`);
  }

  heading('Corrected example');
  if (report.correctedExample) {
    lines.push(`**${report.correctedExample.title}** (${report.correctedExample.generatedBy})`);
    lines.push('');
    lines.push(
      '```' +
        (report.correctedExample.format === 'json'
          ? 'json'
          : report.correctedExample.format === 'yaml'
            ? 'yaml'
            : 'bash'),
    );
    lines.push(report.correctedExample.content);
    lines.push('```');
    if (report.correctedExample.notes.length > 0) {
      lines.push('');
      for (const note of report.correctedExample.notes) lines.push(`> ${note}`);
    }
  } else {
    lines.push('_No corrected example could be produced from this input._');
  }

  heading('Verification checklist');
  for (const item of report.verificationChecklist) lines.push(`- [ ] ${item}`);

  heading('Information still needed');
  if (report.informationStillNeeded.length === 0) lines.push('_Nothing further identified._');
  for (const item of report.informationStillNeeded) lines.push(`- ${item}`);

  heading('Copyable support ticket');
  lines.push('```text');
  lines.push(report.supportTicketSummary);
  lines.push('```');

  heading('Uncertainty and safety');
  lines.push('Uncertainty:');
  for (const note of report.uncertaintyNotes) lines.push(`- ${note}`);
  lines.push('');
  lines.push('Safety:');
  for (const note of report.safetyNotes) lines.push(`- ${note}`);

  heading('Sanitization summary');
  if (report.sanitization.totalRedactions === 0) {
    lines.push('_No sensitive values were detected by the automatic redaction pass._');
  } else {
    lines.push(`${report.sanitization.totalRedactions} value(s) were replaced:`);
    lines.push('');
    for (const hit of report.sanitization.hits) {
      lines.push(`- \`${hit.placeholder}\` × ${hit.count} (${hit.category})`);
    }
  }
  if (report.sanitization.customSensitiveFields.length > 0) {
    lines.push(
      '',
      `Custom sensitive field names: ${report.sanitization.customSensitiveFields.join(', ')}.`,
    );
  }
  lines.push('', `> ${report.sanitization.warning}`);

  heading('Limitations');
  for (const note of report.limitations) lines.push(`- ${note}`);

  lines.push(
    '',
    '---',
    '',
    '_Generated by API Rescue Desk. No request was executed to produce this report._',
  );
  return lines.join('\n');
}
